import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { fetchSelfCredentials } from '../api/keycloak-admin';
import { AUTH_METHODS, type AuthMethod } from '../constants/auth-methods';

/** A user credential enriched with its auth method metadata */
export interface EnrichedCredential {
  id: string;
  type: string;
  createdDate: number;
  method: AuthMethod | undefined;
}

interface UseUserCredentialsResult {
  credentials: EnrichedCredential[];
  loading: boolean;
  refresh: () => void;
}

/** Fetches the current user's credentials via self-service proxy and maps them to AUTH_METHODS entries */
export function useUserCredentials(userId: string | undefined): UseUserCredentialsResult {
  const { token } = useAuth();
  const [credentials, setCredentials] = useState<EnrichedCredential[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!userId || !token) return;
    setLoading(true);
    try {
      const raw = await fetchSelfCredentials(token);
      const enriched = raw.map(cred => ({
        ...cred,
        method: AUTH_METHODS.find(m => m.credentialType === cred.type),
      }));
      setCredentials(enriched);
    } catch (err) {
      console.error('[useUserCredentials] Failed to load:', err);
    } finally {
      setLoading(false);
    }
  }, [userId, token]);

  useEffect(() => { load(); }, [load]);

  return { credentials, loading, refresh: load };
}
